import { shapeEndpoints } from '@/api/endpoints/shapes';
import { useOrgApiClient } from '@/util/orgApiClient';
import { useMemo } from 'react';

export interface ProjectShape {
  id: string;
  type: 'Feature';
  geometry: GeoJSON.Geometry;
  properties: {
    organization: string;
    project: string;
    name: string;
    description: string;
    color: string;
    landAcquisitionCost: string | null;
    address: string;
    countryCode: string;
    areaSqft: number | null;
    lengthFt: number | null;
    intersectingZipcodes: string[];
    cachedUtilityIds: number[];
    utilityProviders: { utilityIdentifier: string; utilityIdentifierType: string; energyType: string; name: string; state: string; countryCode: string; serviceZone: string }[];
    nearestCityData: Record<string, unknown>;
    regionalMultiplier: string | null;
    latitude: number | null;
    longitude: number | null;
    createdBy: number | null;
    updatedBy: number | null;
    createdAt: string;
    updatedAt: string;
  };
}

interface ProjectShapeCollection {
  type: 'FeatureCollection';
  features: ProjectShape[];
}

interface LocationAreaCollection {
  type: 'FeatureCollection';
  features: GeoJSON.Feature[];
}

interface CreateShapePayload {
  type: 'Feature';
  geometry: GeoJSON.Geometry;
  properties: {
    project: string;
    name?: string;
    description?: string;
    color?: string;
    landAcquisitionCost?: number | null;
    address?: string;
  };
}

interface UpdateShapePayload {
  geometry?: GeoJSON.Geometry;
  properties?: Partial<CreateShapePayload['properties']>;
}

export function useShapeService() {
  const { orgFetch, selectedOrg } = useOrgApiClient();
  const orgId = selectedOrg?.id ?? '';

  const service = useMemo(
    () => ({
      list: (projectId: string) =>
        orgFetch<ProjectShapeCollection>(
          shapeEndpoints.LIST(orgId, projectId),
        ),

      create: (projectId: string, payload: CreateShapePayload) =>
        orgFetch<ProjectShape>(
          shapeEndpoints.CREATE(orgId, projectId),
          {
            method: 'POST',
            body: JSON.stringify(payload),
          },
        ),

      get: (projectId: string, shapeId: string) =>
        orgFetch<ProjectShape>(
          shapeEndpoints.DETAIL(orgId, projectId, shapeId),
        ),

      update: (projectId: string, shapeId: string, payload: UpdateShapePayload) =>
        orgFetch<ProjectShape>(
          shapeEndpoints.UPDATE(orgId, projectId, shapeId),
          {
            method: 'PATCH',
            body: JSON.stringify(payload),
          },
        ),

      delete: (projectId: string, shapeId: string) =>
        orgFetch<null>(
          shapeEndpoints.DELETE(orgId, projectId, shapeId),
          { method: 'DELETE' },
        ),

      refresh: (projectId: string, shapeId: string) =>
        orgFetch<ProjectShape>(
          shapeEndpoints.REFRESH(orgId, projectId, shapeId),
          { method: 'POST' },
        ),

      getLocationAreas: (projectId: string, codes: string[]) =>
        orgFetch<LocationAreaCollection>(
          shapeEndpoints.LOCATION_AREAS(orgId, projectId, codes),
        ),
    }),
    [orgFetch, orgId],
  );

  if (!selectedOrg) {
    throw new Error('useShapeService: No organization selected.');
  }

  return service;
}
